import React from 'react';
import { connect } from 'react-redux';
import AdminShell, { Icon } from './AdminShell';
import { Fieldset, SettingRow, Switch, Field, FormActions, FormCard } from './Form';
import { api, staticpageUploadDir } from '../../config';

/*
 * Precaution notice — layar `precaution` paket client.
 *
 * PROTOTIPE MENAMPILKAN DAFTAR; KENYATAANNYA SATU. Prototipe punya lima notice,
 * masing-masing terikat ke sebuah layanan, dengan tombol tambah dan hapus.
 * PrecautionNotification tidak punya kolom layanan, tidak punya mutation hapus,
 * dan — yang menentukan — API mobile membacanya begini:
 *
 *     PrecautionNotification.findOne({ where: { isEnabled: true, id: 1 } })
 *
 * id-nya dipaku. Baris kedua tidak akan pernah dibaca aplikasi mana pun, jadi
 * tombol "Add notice" akan membuat sesuatu yang tidak pernah tampil. Karena itu
 * layar ini menyunting satu notice itu, dan mengatakan kenapa.
 *
 * Kalau nanti notice per layanan memang diinginkan, yang perlu berubah lebih
 * dulu query di API, bukan layar ini.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

// Query-nya mengembalikan SEMUA baris, bukan satu. Yang dipilih di sini baris
// yang benar-benar dibaca aplikasi.
const Q = `{
  getAllPrecautionNotification {
    status errorMessage
    results { id title description titleAr descriptionAr isEnabled imageName }
  }
}`;

const M_SAVE = `mutation ($id: Int, $title: String!, $description: String!, $titleAr: String!, $descriptionAr: String!,
                 $isEnabled: Boolean!, $imageName: String) {
  updatePrecautionNotification(id: $id, title: $title, description: $description,
                               titleAr: $titleAr, descriptionAr: $descriptionAr,
                               isEnabled: $isEnabled, imageName: $imageName) {
    status errorMessage
  }
}`;

const ok = res => res && Number(res.status) === 200;

// Baris yang benar-benar dibaca aplikasi. Dikunci di query API.
const LIVE_ID = 1;

class PrecautionNotice extends React.Component {
  state = {
    id: null, title: '', description: '', titleAr: '', descriptionAr: '', isEnabled: true, imageName: null,
    original: null, loading: true, error: null, saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  load = async () => {
    this.setState({ loading: true, error: null });
    const json = await gql(Q).catch(() => null);
    const res = json && json.data && json.data.getAllPrecautionNotification;
    const rows = (res && res.results) || [];
    // Baris id 1 kalau ada; kalau tidak, yang pertama — supaya layarnya tetap
    // bisa dipakai sambil tetap memperingatkan bahwa itu bukan yang dibaca app.
    const r = rows.find(x => Number(x.id) === LIVE_ID);
    if (!ok(res) || !r) {
      const arabic = String(this.props.currentLocale || '').startsWith('ar');
      this.setState({ loading: false, error: arabic
        ? 'لم يتم العثور على إشعار التحذير المباشر (المعرّف 1).'
        : (res && res.errorMessage) || 'Could not load the live notice (id 1).' });
      return;
    }
    this.setState({
      id: r.id, title: r.title || '', description: r.description || '',
      titleAr: r.titleAr || '', descriptionAr: r.descriptionAr || '',
      isEnabled: !!r.isEnabled, imageName: r.imageName || null,
      original: { title: r.title || '', description: r.description || '', titleAr: r.titleAr || '', descriptionAr: r.descriptionAr || '', isEnabled: !!r.isEnabled },
      loading: false
    });
  };

  set = (key, value) => this.setState({ [key]: value, notice: null });

  dirty() {
    const { original, title, description, titleAr, descriptionAr, isEnabled } = this.state;
    if (!original) return false;
    return original.title !== title
      || original.description !== description
      || original.titleAr !== titleAr
      || original.descriptionAr !== descriptionAr
      || original.isEnabled !== isEnabled;
  }

  reset = () => this.setState(st => ({ ...st.original, notice: null }));

  save = async () => {
    const { id, title, description, titleAr, descriptionAr, isEnabled, imageName } = this.state;
    const arabic = String(this.props.currentLocale || '').startsWith('ar');
    if (!title.trim() || !description.trim() || !titleAr.trim() || !descriptionAr.trim()) {
      this.setState({ notice: { tone: 'danger', text: arabic
        ? 'العنوان والنص باللغتين الإنجليزية والعربية مطلوبان.'
        : 'English and Arabic heading and body text are required.' } });
      return;
    }
    this.setState({ saving: true });
    const json = await gql(M_SAVE, {
      id: id || undefined,
      title: title.trim(),
      description,
      titleAr: titleAr.trim(),
      descriptionAr,
      isEnabled: !!isEnabled,
      // Dikirim kembali apa adanya: mutation menulis kolom ini setiap kali, dan
      // menghilangkannya akan mengosongkan gambarnya.
      imageName: imageName || undefined
    }).catch(() => null);
    const res = json && json.data && json.data.updatePrecautionNotification;
    this.setState({ saving: false });

    if (!ok(res)) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage)
        || (arabic ? 'تعذر حفظ إشعار التحذير.' : 'Could not save the notice.') } });
      return;
    }
    this.setState({
      original: { title: title.trim(), description, titleAr: titleAr.trim(), descriptionAr, isEnabled: !!isEnabled },
      title: title.trim(),
      titleAr: titleAr.trim(),
      notice: {
        tone: 'info',
        text: arabic
          ? (isEnabled ? 'تم الحفظ. سيظهر الإشعار للعملاء أثناء انتظار مقدم الخدمة.'
            : 'تم الحفظ وإيقاف الإشعار. لن يرى العملاء أي إرشادات للسلامة.')
          : (isEnabled ? 'Saved. Customers see this while they wait for a provider.'
            : 'Saved, and switched off — customers now see no safety notice at all.')
      }
    });
  };

  render() {
    const {
      id, title, description, titleAr, descriptionAr, isEnabled, imageName, loading, error, saving, notice
    } = this.state;
    const isArabic = String(this.props.currentLocale || '').startsWith('ar');
    const copy = isArabic ? {
      breadcrumb: ['الاتصالات', 'إشعارات التحذير'], titlePage: 'إشعار التحذير',
      lead: 'إرشادات السلامة التي يراها العملاء أثناء انتظار مقدم الخدمة.', classic: 'فتح النسخة الكلاسيكية',
      dismiss: 'إغلاق', info: 'يوجد إشعار واحد فقط، وليس قائمة. تقرأ التطبيقات الإشعار بالمعرّف الثابت 1، لذلك لن يظهر أي إشعار ثانٍ. إشعارات كل خدمة تتطلب تغييرًا أولًا في واجهة برمجة تطبيقات الهاتف.',
      disabled: 'تم إيقاف هذا الإشعار، لذلك لا يرى العملاء الذين ينتظرون مقدم الخدمة أي إرشادات للسلامة.',
      loading: 'جارٍ التحميل…', live: 'هذا هو الإشعار الذي تقرؤه التطبيقات', other: rowId => `تحذير: التطبيقات تقرأ الإشعار بالمعرّف 1، بينما هذا الصف بالمعرّف ${rowId}`,
      save: 'حفظ الإشعار', cancel: 'إلغاء', saving: 'جارٍ الحفظ…', section: 'الإشعار', heading: 'العنوان (بالإنجليزية)', headingHint: 'يظهر بخط عريض في أعلى الإشعار.', titlePlaceholder: 'Attention!',
      headingAr: 'العنوان (بالعربية)', body: 'النص (بالإنجليزية، HTML)', bodyHint: 'يُرسل إلى التطبيقات بصيغة HTML بعد تنظيفه من الوسوم والخصائص غير الآمنة.', bodyAr: 'النص (بالعربية، HTML)', image: 'الصورة التوضيحية', imageSet: 'تم تعيين صورة', noImage: 'لا توجد صورة', imageHint: 'استبدال الصورة يتم من الشاشة الكلاسيكية؛ التحميل يتم إلى خادم API.', visibility: 'الظهور', show: 'إظهار للعملاء', showHint: 'عند إيقافه، لا تستلم التطبيقات شيئًا ولا تعرض إرشادات للسلامة.', required: 'العنوان والنص باللغتين مطلوبان.'
    } : {
      breadcrumb: ['Communications', 'Precaution notices'], titlePage: 'Precaution notice',
      lead: 'Safety guidance shown to customers while they wait for a provider.', classic: 'Open in classic',
      dismiss: 'Dismiss', info: 'There is one notice, not a list. The apps fetch it by fixed id 1, so a second notice would never be shown. Per-service notices require a mobile API change first.',
      disabled: 'This notice is switched off, so customers waiting for a provider see no safety guidance.',
      loading: 'Loading…', live: 'This is the notice the apps read', other: rowId => `Warning: the apps read notice id 1, but this row is id ${rowId}`,
      save: 'Save notice', cancel: 'Cancel', saving: 'Saving…', section: 'Notice', heading: 'Heading (English)', headingHint: 'Shown in bold at the top of the notice.', titlePlaceholder: 'Attention!',
      headingAr: 'Heading (Arabic)', body: 'Body text (English, HTML)', bodyHint: 'Served to the apps as HTML after unsafe tags and attributes are removed.', bodyAr: 'Body text (Arabic, HTML)', image: 'Illustration', imageSet: 'Image set', noImage: 'No image', imageHint: 'Replacing the image is still done on the classic screen; upload goes to the API host.', visibility: 'Visibility', show: 'Show to customers', showHint: 'When switched off, the apps receive nothing and show no safety notice.', required: 'English and Arabic heading and body text are required.'
    };

    const imageUrl = imageName
      ? `${api.apiEndpoint}${staticpageUploadDir}large_${imageName}`
      : null;

    return (
      <AdminShell
        current="precaution"
        breadcrumb={copy.breadcrumb}
        title={copy.titlePage}
        lead={copy.lead}
        actions={[
          <a className="btn ghost" href="/siteadmin/precaution-notification" key="c">
            {copy.classic}
          </a>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{copy.dismiss}</button>
          </div>
        )}

        {/* Bukan pilihan tampilan: aplikasi membaca id 1 secara harfiah. */}
        <div className="notice">
          <span className="ni">i</span>
          <span className="nt">
            {copy.info}
          </span>
        </div>

        {!loading && !isEnabled && (
          <div className="notice danger">
            <span className="ni">!</span>
            <span className="nt">
              {copy.disabled}
            </span>
          </div>
        )}

        {error && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{error}</span>
          </div>
        )}

        {loading
          ? <section className="card"><div className="card-b">{copy.loading}</div></section>
          : (
            <FormCard
              actions={
                <FormActions
                  note={id === LIVE_ID
                    ? copy.live
                    : copy.other(id)}
                  dirty={this.dirty()}
                  busy={saving}
                  onSave={this.save}
                  onCancel={this.reset}
                  saveLabel={copy.save}
                  cancelLabel={copy.cancel}
                  busyLabel={copy.saving}
                />
              }
            >
              <Fieldset title={copy.section}>
                <Field label={copy.heading} required hint={copy.headingHint}>
                  <input className="inp" value={title} placeholder="Attention!"
                    onChange={e => this.set('title', e.target.value)} />
                </Field>
                <Field label={copy.image}>
                  {imageUrl
                    ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                        {/* Gambarnya dilayani host API, bukan server ini, jadi
                            bisa gagal dimuat di lingkungan mana pun yang tidak
                            bisa menjangkaunya. Ikon rusak bawaan peramban
                            terbaca seperti gambarnya hilang; disembunyikan. */}
                        <img src={imageUrl} alt="" style={{
                          width: 64, height: 44, objectFit: 'cover',
                          borderRadius: 'var(--r-sm)', border: '1px solid var(--line)'
                        }} onError={e => { e.target.style.display = 'none'; }} />
                        <span className="tag amber"><i />{copy.imageSet}</span>
                      </span>
                    )
                    : <span className="tag"><i />{copy.noImage}</span>}
                  {/* Unggahannya menuju /uploadStaticBannerImage di host API,
                      bukan ke server ini, jadi tidak dibuat ulang di sini. */}
                  <span className="hint">
                    {copy.imageHint}
                  </span>
                </Field>
                <Field label={copy.headingAr} required span hint={copy.required}>
                  <input className="inp" dir="rtl" value={titleAr} placeholder="تنبيه!"
                    onChange={e => this.set('titleAr', e.target.value)} />
                </Field>
                <Field label={copy.body} required span hint={copy.bodyHint}>
                  <textarea className="inp" rows={12} value={description}
                    onChange={e => this.set('description', e.target.value)} />
                </Field>
                <Field label={copy.bodyAr} required span hint={copy.bodyHint}>
                  <textarea className="inp" dir="rtl" rows={12} value={descriptionAr}
                    onChange={e => this.set('descriptionAr', e.target.value)} />
                </Field>
              </Fieldset>

              <Fieldset title={copy.visibility} rows>
                <SettingRow label={copy.show} hint={copy.showHint}>
                  <Switch checked={isEnabled} label={copy.show}
                    onChange={v => this.set('isEnabled', v)} />
                </SettingRow>
              </Fieldset>
            </FormCard>
          )}
      </AdminShell>
    );
  }
}

const mapState = state => ({ currentLocale: state && state.intl && state.intl.locale });

export default connect(mapState)(PrecautionNotice);
